import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Empty, Flex, Card, Col, Row, Button } from "antd"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import BookingEmptyImage from "ui/Icon/DSIcon/BookingEmptyImage"
import NoMatchesImage from "ui/Icon/DSIcon/NoMatchesImage"
import { getGuideBookingsV2 } from "store/bookings"
import { fetchReservations } from "store/reservations"
import { fetchUpcomingExperiences, fetchExperienceFilters } from "store/upcomingExperiences"
import { fetchExperiences } from "store/experiences"
import { getCalendarCrew } from "store/staff"
import { getCalendarEquipment } from "store/calendar"
import moment from "moment"
import { getStatusMessages, FUTURE_ONLY_STATUSES, DEFAULT_SORT_KEY, DEFAULT_SORT_ORDER } from "./constants"
import BookingsHeader from "./Header"
import BookingTable from "./BookingTable"
import BookingTag from "./BookingTag"
import styles from "./Index.module.css"
import { debounce } from "lodash"
import UpcomingMobileList from "./UpcomingMobileList"
import ReservationMobileList from "./ReservationMobileList"
import ReservationStatusTabs from "./ReservationStatusTabs"
import CustomDrawer from "ui/Drawer"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import LoadMoreButton from "components/common/LoadMore"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { Text } from "ui/Typography"
import { routes } from "router"
import { DollarSign } from "ui/Icon/DSIcon/DollarSign"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { isFeatureEnabled } from "config/features"

const DELAY_TIME = 300

// Check-in/out is implemented but hidden behind the CHECK_IN_OUT flag until the client signs off.
const isCheckInEnabled = isFeatureEnabled("CHECK_IN_OUT")

const snapDateToRange = (dateStr, rangeValue) => {
  if (!dateStr || !rangeValue) return dateStr
  const unit = rangeValue === "day" ? "day" : rangeValue
  const parsed = moment(dateStr, DATE_FORMAT)
  return parsed.isValid() ? parsed.startOf(unit).format(DATE_FORMAT) : dateStr
}

// When switching Day/Week/Month, re-anchor the date. If today falls inside the period
// the user is currently viewing, land on today's week/day (so Month "July" → Week shows
// today's week, not the week straddling July 1 back into June). Otherwise the user has
// navigated away, so just snap their existing date to the new range's start.
const reanchorDateForRange = (dateStr, prevRange, nextRange) => {
  const parsed = moment(dateStr, DATE_FORMAT)
  if (!parsed.isValid()) return dateStr
  const nextUnit = nextRange === "day" ? "day" : nextRange
  const prevUnit = prevRange === "day" ? "day" : prevRange
  if (prevUnit && moment().isSame(parsed, prevUnit)) {
    return moment().startOf(nextUnit).format(DATE_FORMAT)
  }
  return parsed.startOf(nextUnit).format(DATE_FORMAT)
}

const getDefaultParams = () => ({
  range: "month",
  date: moment().startOf("month").format(DATE_FORMAT),
  sort: "asc",
  search: ""
})

const buildDateParams = ({ range, date, dateFrom, dateTo }) => {
  if (range === "custom") return { date_from: dateFrom, date_to: dateTo }
  const [year, month, day] = (date || "").split("-")
  switch (range) {
    case "day":
      return { day, month, year }
    case "week":
      return {
        date_from: moment(date, DATE_FORMAT).startOf("week").format(DATE_FORMAT),
        date_to: moment(date, DATE_FORMAT).endOf("week").format(DATE_FORMAT)
      }
    case "month":
      return { month, year }
    case "year":
      return { year }
    default:
      return {}
  }
}

const Bookings = ({ bookingType = "booked" }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const statusMessages = getStatusMessages(t)
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  // Reservation listing offers [15, 25, 50, 100]; booked/upcoming offers [10, 20, …]. Seed the
  // default to the active tab's smallest option so the rows-per-page selector matches what the API
  // is actually asked for on first load (10 wasn't a valid reservation option, so the select showed
  // 15 while per_page stayed 10).
  const [pageSize, setPageSize] = useState(bookingType === "reservation" ? 15 : 10)
  // null = no column actively sorted; the request still falls back to the default sort below.
  const [sortKey, setSortKey] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [isLegendOpen, setIsLegendOpen] = useState(false)
  const range = searchParams?.get("range")
  const type = bookingType
  const date = searchParams?.get("date")
  const sort = searchParams?.get("sort")
  const search = searchParams?.get("search")
  const dateFrom = searchParams?.get("date_from")
  const dateTo = searchParams?.get("date_to")
  const navigate = useNavigate()
  const openFiltersRef = useRef(null)
  const isReservation = bookingType === "reservation"
  // Blackout "Review" links (Settings, Calendar) deep-link here with the listings and resources a
  // blackout affects. Both are read into the initial state rather than applied by an effect, so the
  // very first request is already filtered — an effect lands a render late, after an unfiltered
  // request has gone out and can race the filtered one back.
  const [urlExperienceIds] = useState(() => searchParams.getAll("experiences[]"))
  const [reservationFilters, setReservationFilters] = useState(() => ({
    payment: null,
    waivers: null,
    checkIn: null,
    experiences: urlExperienceIds
  }))
  // Resource filter (Upcoming Experiences): selection keys like "crew-1" / "equip-5". The link's
  // keys arrive already type-prefixed, which is how this filter stores them.
  const [selectedResources, setSelectedResources] = useState(() => searchParams.getAll("resources[]"))

  // Slice and loader-watcher key depend on which tab is active
  const storeKey = isReservation ? "reservations" : "upcomingExperiences"
  useLoaderWatchers({ watchers: [storeKey] })

  const reservationsState = useSelector((store) => store.reservations)
  const upcomingState = useSelector((store) => store.upcomingExperiences)
  const bookingsState = useSelector((store) => store.bookings)

  // Real crew/equipment pools for the Resources filter (Upcoming Experiences)
  const crewOptions = useSelector((store) => store.staff.calendarCrew || [])
  const equipmentOptions = useSelector((store) =>
    (store.calendar.calendarEquipment || []).map((eq) => ({ id: eq.id, name: eq.equipment_title || eq.name || "" }))
  )

  // Experience pool for the Reservations experience filter, from the embedded
  // experiences list endpoint (returns { id, title }).
  const experienceList = useSelector((store) => store.experiences?.list || [])

  const { items, loading, metadata } = isReservation ? reservationsState : upcomingState

  // All Reservations lifecycle sub-tab (Upcoming/Requests/Completed/Cancelled). Defaults to
  // "upcoming". Counts come from the list metadata (backend: metadata.status_counts).
  const reservationStatus = searchParams?.get("status") || "upcoming"
  const statusCounts = metadata?.status_counts || {}

  const handleStatusChange = useCallback(
    (next) => {
      // Upcoming/Requests are future-only (backend welds booked_date >= today). If the user
      // carries over a fully-past date from Completed/Cancelled, the tab would load empty — so
      // snap the anchor (and any custom range) forward to the current period.
      const isFutureOnly = FUTURE_ONLY_STATUSES.includes(next)
      setPage(1)
      setSelectedRowKeys([])
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev.toString())
        params.set("status", next)
        if (isFutureOnly) {
          const rangeValue = params.get("range")
          if (rangeValue === "custom") {
            // A custom range can point into the past and isn't valid on a future-only tab.
            // Fully exit custom mode back to the default Month view (dropping bounds only would
            // leave range="custom" with no dates → the switcher renders an empty "—").
            params.set("range", "month")
            params.set("date", moment().startOf("month").format(DATE_FORMAT))
            params.delete("date_from")
            params.delete("date_to")
          } else {
            const unit = rangeValue === "day" ? "day" : rangeValue || "month"
            const anchor = params.get("date")
            if (anchor && moment(anchor, DATE_FORMAT).endOf(unit).isBefore(moment(), "day")) {
              params.set("date", moment().startOf(unit).format(DATE_FORMAT))
            }
          }
        }
        return params
      })
    },
    [setSearchParams]
  )

  // Experience filter options.
  // Reservations use the embedded experiences list endpoint ({ id, title }); filtering is by id.
  // Upcoming Experiences use the server-provided filter list, falling back to distinct row titles.
  // Split by tab so the reservation options depend on the pool alone: sharing a memo with the
  // Upcoming fallback made them a new array on every response, which re-ran the URL re-seed below.
  const reservationExperienceOptions = useMemo(
    () =>
      experienceList
        .filter((exp) => exp?.id && exp?.title)
        .map((exp) => ({ value: exp.id, label: exp.title })),
    [experienceList]
  )
  const upcomingExperienceOptions = useMemo(() => {
    if (isReservation) return []
    if (upcomingState.experienceFilters?.length) return upcomingState.experienceFilters
    const seen = new Set()
    const options = []
    items.forEach((item) => {
      const title = item?.tripTitle
      if (title && !seen.has(title)) {
        seen.add(title)
        options.push(title)
      }
    })
    return options
  }, [isReservation, items, upcomingState.experienceFilters])
  const experienceOptions = isReservation ? reservationExperienceOptions : upcomingExperienceOptions

  const reservationFilterCount = useMemo(
    () => [reservationFilters.payment, reservationFilters.waivers, isCheckInEnabled && reservationFilters.checkIn]
      .filter(Boolean).length,
    [reservationFilters]
  )

  const handleApplyExperiences = useCallback((experiences) => {
    setReservationFilters((prev) => ({ ...prev, experiences }))
  }, [])

  // The URL carries experience ids as strings while the pool keys them as numbers, so the seeded
  // ids filter correctly but leave the picker showing nothing ticked. Swap in the pool's own values
  // once, when it lands; the request is keyed on the string form, so the swap does not refetch.
  const hasSeededUrlExperiencesRef = useRef(false)
  useEffect(() => {
    if (hasSeededUrlExperiencesRef.current || !reservationExperienceOptions.length) return
    hasSeededUrlExperiencesRef.current = true
    if (!urlExperienceIds.length) return
    const matched = reservationExperienceOptions
      .filter((option) => urlExperienceIds.includes(String(option.value)))
      .map((option) => option.value)
    if (matched.length) setReservationFilters((prev) => ({ ...prev, experiences: matched }))
  }, [reservationExperienceOptions, urlExperienceIds])

  // The experience filter as the request sends it. Keyed on the sorted string form so `[12]` and
  // `["12"]` — the pool's ids and the URL's — are one filter, not two requests; sorted because the
  // re-seed returns them in pool order, not the link's.
  const experiencesKey = (reservationFilters.experiences || []).map(String).sort().join(",")
  const requestExperienceIds = useMemo(() => (experiencesKey ? experiencesKey.split(",") : []), [experiencesKey])

  // Ant Design cycles a sortable header ascend → descend → cancelled, reporting the cancel as
  // `order: undefined`. Clear the sort on that (rather than reading it as "desc") so the third
  // click really does cancel: no header renders as sorted and the request falls back to the
  // default sort.
  const onSortChange = (pagination, filter, sorter) => {
    if (!sorter?.order) {
      setSortKey(null)
      setSortOrder(null)
    } else {
      setSortKey(sorter.field || sorter.columnKey || DEFAULT_SORT_KEY)
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc")
    }
    setPage(1)
  }

  // Keep setSearchParams behind a ref so the debounced fn can be created ONCE.
  // react-router's setSearchParams is not guaranteed stable; putting it in the
  // useMemo deps recreated the debounce each render, so keystrokes never shared
  // a single timer and every character fired its own request.
  const setSearchParamsRef = useRef(setSearchParams)
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams
  }, [setSearchParams])

  const debouncedUpdate = useMemo(
    () =>
      debounce((value) => {
        setSearchParamsRef.current((prev) => {
          const params = new URLSearchParams(prev.toString())
          params.set("search", value)
          return params
        })
      }, DELAY_TIME),
    []
  )

  const onChangeFilters = useCallback(
    ({ target }) => {
      setPage(1)
      if (target.name === "search") {
        debouncedUpdate(target.value)
      } else {
        setSearchParams((prev) => {
          const updatedParams = new URLSearchParams(prev.toString())
          if (target.name === "range") {
            const prevRange = updatedParams.get("range")
            const currentDate = updatedParams.get("date")
            updatedParams.set("range", target.value)
            if (currentDate) updatedParams.set("date", reanchorDateForRange(currentDate, prevRange, target.value))
            updatedParams.delete("date_from")
            updatedParams.delete("date_to")
          } else {
            updatedParams.set(target.name, target.value)
            if (target.name === "date") updatedParams.set("date", snapDateToRange(target.value, updatedParams.get("range")))
          }
          return updatedParams
        })
      }
    },
    [debouncedUpdate, setSearchParams]
  )

  useEffect(() => {
    return () => debouncedUpdate.cancel()
  }, [debouncedUpdate])

  const fetchData = useCallback(
    (nextPage = false, pageOverride = null) => {
      if (!range || !type || !sort) return
      if (range === "custom") {
        if (!dateFrom || !dateTo) return
      } else {
        if (!date) return
      }
      setSelectedRowKeys([])

      // A search looks across all dates: a booking ID or client name is rarely in the period shown.
      const dateParams = search ? {} : buildDateParams({ range, date, dateFrom, dateTo })
      const requestedPage = pageOverride || page

      // Split resource selection keys ("crew-1" / "equip-5") into id lists per type. Both
      // listings carry the Resources filter, so both send them.
      // NOTE: crew_ids[]/equipment_ids[] param names are assumed (mirrors the assign
      // endpoints) — the list-filter contract for resources is not in the API doc yet.
      const crewIds = selectedResources.filter((key) => key.startsWith("crew-")).map((key) => key.slice(5))
      const equipmentIds = selectedResources.filter((key) => key.startsWith("equip-")).map((key) => key.slice(6))

      if (isReservation) {
        const params = {
          ...dateParams,
          search,
          status: reservationStatus,
          sort_by: sortKey || DEFAULT_SORT_KEY,
          order: sortOrder || DEFAULT_SORT_ORDER,
          per_page: pageSize,
          page: requestedPage,
          ...(reservationFilters.payment && { payment: reservationFilters.payment }),
          ...(reservationFilters.waivers && { waivers: reservationFilters.waivers }),
          ...(isCheckInEnabled && reservationFilters.checkIn && { check_in: reservationFilters.checkIn }),
          ...(requestExperienceIds.length && { "experiences[]": requestExperienceIds }),
          ...(crewIds.length && { "crew_ids[]": crewIds }),
          ...(equipmentIds.length && { "equipment_ids[]": equipmentIds })
        }
        dispatch(fetchReservations(params, Boolean(nextPage)))
      } else {
        const params = {
          ...dateParams,
          search,
          sort_by: sortKey || DEFAULT_SORT_KEY,
          order: sortOrder || DEFAULT_SORT_ORDER,
          per_page: pageSize,
          page: requestedPage,
          ...(crewIds.length && { "crew_ids[]": crewIds }),
          ...(equipmentIds.length && { "equipment_ids[]": equipmentIds })
        }
        dispatch(fetchUpcomingExperiences(params, Boolean(nextPage)))
      }
    },
    [
      date,
      dateFrom,
      dateTo,
      range,
      type,
      sort,
      search,
      reservationStatus,
      sortOrder,
      sortKey,
      page,
      pageSize,
      isReservation,
      reservationFilters.payment,
      reservationFilters.waivers,
      reservationFilters.checkIn,
      requestExperienceIds,
      selectedResources
    ]
  )

  // Fetch experience filter options once for upcoming experiences
  useEffect(() => {
    if (!isReservation) dispatch(fetchExperienceFilters())
  }, [isReservation])

  // Fetch the experience pool for the Reservations experience filter
  useEffect(() => {
    if (isReservation) dispatch(fetchExperiences("calendar", null, "id,title"))
  }, [isReservation])

  // Fetch crew/equipment pools for the Resources filter (shown on both By Experience and
  // By Reservation)
  useEffect(() => {
    if (!crewOptions.length) dispatch(getCalendarCrew())
    if (!equipmentOptions.length) dispatch(getCalendarEquipment())
  }, [])

  const handleLoadMore = useCallback(() => {
    if (!metadata?.next_page) return
    fetchData(true, metadata.next_page)
  }, [fetchData, metadata?.next_page])

  useEffect(() => {
    const hasRequiredParams = searchParams.get("range") && searchParams.get("date") && searchParams.get("sort")
    if (!hasRequiredParams) {
      setSearchParams(getDefaultParams())
    }
  }, [bookingType, searchParams, setSearchParams])

  useEffect(() => {
    fetchData(false)
  }, [fetchData])

  // "Active filters" = anything narrowing the list: search, date range, resources, or any of the
  // reservation filters (payment / waivers / check-in / experiences). Drives the empty state.
  const hasActiveFilters = !!(
    search ||
    (dateFrom && dateTo) ||
    selectedResources.length ||
    reservationFilters.payment ||
    reservationFilters.waivers ||
    (isCheckInEnabled && reservationFilters.checkIn) ||
    reservationFilters.experiences?.length
  )

  const handleClearAll = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev.toString())
      params.delete("search")
      params.delete("date_from")
      params.delete("date_to")
      return params
    })
    setSelectedResources([])
    setReservationFilters({ payment: null, waivers: null, checkIn: null, experiences: [] })
  }, [setSearchParams])

  const emptyFallback = (() => {
    if (loading) return <></>
    if (hasActiveFilters) {
      return (
        <div className={styles.contentWrapper}>
          <Flex vertical align="center" gap={12}>
            <span className={styles.noMatchesCircle}>
              <NoMatchesImage width={100} height={100} />
            </span>
            <span className={styles.noMatchesText}>No matches for current search and filters</span>
            <Button type="primary" shape="round" onClick={handleClearAll}>
              Clear All
            </Button>
          </Flex>
        </div>
      )
    }
    return (
      <div className={styles.contentWrapper}>
        <Empty
          image={<BookingEmptyImage />}
          description={
            <>
              <Text strong size="lg">
                {statusMessages[type]?.title}
              </Text>
              <p className={styles.emptyDescription}>{statusMessages[type]?.description}</p>
            </>
          }
        />
      </div>
    )
  })()

  // The legacy BookingListing (mobile) still reads from bookings store for upcoming experiences
  // until that view is also migrated; reservations mobile view is not used currently
  const legacyBookingsState = bookingsState
  const legacyMetadata = isReservation ? metadata : legacyBookingsState.metadata

  return (
    <FirstLoading name={storeKey}>
      <>
        <div className={styles.stickyHeader}>
          <Flex align="center" justify="space-between" className={styles.pageHeadingRow}>
            <h1 className={styles.pageHeading}>
              {type === "reservation"
                ? t("pro.sidebar.menu.bookings_by_reservation", "By Reservation")
                : t("pro.sidebar.menu.bookings_by_experience", "By Experience")}
            </h1>
            {type === "reservation" ? (
              <Button type="primary" shape="round" onClick={() => navigate(routes.guideBookingNewPath())}>
                {t("bookings.add", "New Booking")}
              </Button>
            ) : (
              <>
                <Flex align="center" gap={16} className={styles.legend}>
                  <span className={styles.legendLabel}>Legend:</span>
                  <Flex align="center" gap={4}><BookingTag color="success"><DollarSign stroke="#45bf6b" /></BookingTag><span>Paid</span></Flex>
                  <Flex align="center" gap={4}><BookingTag color="warning"><DollarSign stroke="#F98600" /></BookingTag><span>Balance due</span></Flex>
                  <Flex align="center" gap={4}><BookingTag color="error"><DollarSign stroke="#ef4444" /></BookingTag><span>Payment failed</span></Flex>
                  <Flex align="center" gap={4}><BookingTag color="blue"><DollarSign stroke="#1d72f4" /></BookingTag><span>Partial refund</span></Flex>
                </Flex>
                <button type="button" className={styles.legendMobileBtn} onClick={() => setIsLegendOpen(true)}>
                  <ExclamationCircleOutlined /> Legend
                </button>
              </>
            )}
          </Flex>
          {isReservation && (
            <ReservationStatusTabs status={reservationStatus} counts={statusCounts} onChange={handleStatusChange} />
          )}
          <BookingsHeader
            onChangeFilters={onChangeFilters}
            variant={isReservation ? "reservation" : "upcoming"}
            reservationStatus={reservationStatus}
            onOpenFilters={openFiltersRef}
            experienceOptions={experienceOptions}
            selectedExperiences={reservationFilters.experiences}
            onApplyExperiences={handleApplyExperiences}
            selectedResources={selectedResources}
            onApplyResources={setSelectedResources}
            crewOptions={crewOptions}
            equipmentOptions={equipmentOptions}
            filterCount={reservationFilterCount}
          />
        </div>
        <div className={styles.bookingsContainer} id="bookings">
          <WithEmptyStateWrapper
            name="Booking"
            hasItems={items.length > 0 || isReservation}
            fallbackTitle={"abc"}
            fallback={emptyFallback}
          >
            <Card className="mb-m" styles={{ body: { padding: "0px !important" } }}>
              <Row gutter={[0, 16]}>
                <Col xs={24} md={0}>
                  <Flex vertical gap={15}>
                    {isReservation ? (
                      <ReservationMobileList
                        items={items}
                        onView={(item) => navigate(routes.guideReservationDetailPath({ id: item.id }))}
                      />
                    ) : (
                      <UpcomingMobileList
                        items={items}
                        onViewDetails={(item) => navigate(routes.guideBookingDetailsPath({ id: item.uuid }))}
                      />
                    )}
                    <LoadMoreButton
                      currentPage={metadata?.page || 1}
                      totalPages={Math.ceil((metadata?.total_results || 0) / pageSize)}
                      loading={loading}
                      onLoadMore={handleLoadMore}
                      totalAvailable={metadata?.total_results || 0}
                    />
                  </Flex>
                </Col>
                <Col xs={0} md={24}>
                  <BookingTable
                    type={type}
                    reservationStatus={reservationStatus}
                    page={page}
                    pageSize={pageSize}
                    sortKey={sortKey}
                    sortOrder={sortOrder}
                    fetchBookings={fetchData}
                    onPageChange={setPage}
                    onPageSizeChange={(newSize) => {
                      setPageSize(newSize)
                      setPage(1)
                    }}
                    onSortChange={onSortChange}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    onOpenFilters={openFiltersRef}
                    reservationFilters={reservationFilters}
                    onReservationFiltersChange={setReservationFilters}
                    experienceOptions={experienceOptions}
                  />
                </Col>
              </Row>
            </Card>
          </WithEmptyStateWrapper>
        </div>

        <CustomDrawer open={isLegendOpen} setOpen={setIsLegendOpen} title="Legend">
          <Flex vertical gap={16} className={styles.legendSheet}>
            <Flex align="center" gap={8}><BookingTag color="success"><DollarSign stroke="#45bf6b" /></BookingTag><span>Paid</span></Flex>
            <Flex align="center" gap={8}><BookingTag color="warning"><DollarSign stroke="#F98600" /></BookingTag><span>Balance due</span></Flex>
            <Flex align="center" gap={8}><BookingTag color="error"><DollarSign stroke="#ef4444" /></BookingTag><span>Payment failed</span></Flex>
            <Flex align="center" gap={8}><BookingTag color="blue"><DollarSign stroke="#1d72f4" /></BookingTag><span>Partial refund</span></Flex>
          </Flex>
        </CustomDrawer>
      </>
    </FirstLoading>
  )
}

Bookings.propTypes = {
  bookingType: PropTypes.oneOf(["booked", "reservation"])
}

export default Bookings
